import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import BreadcrumbBack from '../components/BreadcrumbBack';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface ShortItem {
  id: string;
  index: number;
  title: string;
  caption: string;
  tags: string[];
  score: number;
  startTime: number;
  endTime: number;
  duration: number;
  fileUrl: string;
  platform: string;
}

interface ProjectData {
  id: string;
  name: string;
}

interface VideoData {
  id: string;
  name: string | null;
  file_name: string;
  file_path: string;
  duration: number | null;
  platform: string;
  project_id: string;
  job_id: string | null;
  job_status: string;
  shorts: ShortItem[];
  transcript: {
    fullText: string;
    segments: any;
    silenceTimes: any;
    transcriptFileKey?: string | null;
    subtitleDownloadUrl?: string | null;
  } | null;
  analysis: { topics: any[]; highlights: any; sceneFrames: number[] } | null;
}

const VideoEditor: React.FC = () => {
  const { projectId, videoId } = useParams<{ projectId: string; videoId: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<ProjectData | null>(null);
  const [video, setVideo] = useState<VideoData | null>(null);
  const [loading, setLoading] = useState(true);
  const [copying, setCopying] = useState(false);
  const [activeTab, setActiveTab] = useState<'shorts' | 'transcript' | 'analysis'>('shorts');
  const [selectedShort, setSelectedShort] = useState<string | null>(null);
  const [editField, setEditField] = useState<{ id: string; field: string; value: string } | null>(null);
  useEffect(() => {
    // placeholder for future outside-click handlers
    return () => {};
  }, []);

  const fetchVideo = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/projects/${projectId}/videos/${videoId}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setVideo(data);
        if (data.shorts?.length > 0 && !selectedShort) setSelectedShort(data.shorts[0].id);
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, [projectId, videoId, selectedShort]);

  useEffect(() => { fetchVideo(); }, [fetchVideo]);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/projects/${projectId}`, { credentials: 'include' });
        if (res.ok) setProject(await res.json());
      } catch { /* ignore */ }
    };
    fetchProject();
  }, [projectId]);

  const handleCopyToMedia = async () => {
    if (copying) return;
    setCopying(true);
    try {
      const res = await fetch(`${API_BASE}/api/projects/${projectId}/videos/${videoId}/copy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (res.ok) {
        navigate(`/projects/${projectId}`);
      } else {
        const err = await res.json();
        alert(err.error || 'Failed to regenerate shorts');
      }
    } catch {
      alert('Failed to regenerate shorts');
    }
    setCopying(false);
  };

  const handleSaveField = async (shortId: string, field: string, value: string) => {
    try {
      const body: any = {};
      if (field === 'tags') {
        body.tags = value.split(',').map(t => t.trim()).filter(Boolean);
      } else {
        body[field] = value;
      }
      const res = await fetch(`${API_BASE}/api/shorts/${shortId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const updated = await res.json();
        setVideo(prev => prev ? {
          ...prev,
          shorts: prev.shorts.map(s => s.id === shortId ? {
            ...s,
            title: updated.title,
            caption: updated.caption,
            tags: typeof updated.tags === 'string' ? JSON.parse(updated.tags) : updated.tags,
          } : s),
        } : prev);
      }
    } catch { /* ignore */ }
    setEditField(null);
  };

  const selected = video?.shorts.find(s => s.id === selectedShort) ?? null;

  const formatTime = (sec: number) => {
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const normalizeShortScore = (score: number) => {
    const numericScore = Number(score);
    if (!Number.isFinite(numericScore)) return 0;
    const normalized = numericScore <= 10 ? numericScore * 10 : numericScore;
    return Math.max(0, Math.min(100, Math.round(normalized)));
  };

  if (loading) return <div className="dashboard-layout"><Sidebar /><main className="main-content"><Header /><div className="project-loading">Loading...</div></main></div>;

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="main-content">
        <Header />

        {/* Breadcrumb with actions on the right */}

        <div className="breadcrumb" style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 20}}>
          <div className="breadcrumb-links">
            <BreadcrumbBack to={`/projects/${projectId}`} />
            <Link to="/projects">Projects</Link>
            <span className="breadcrumb-sep">›</span>
            <Link to={`/projects/${projectId}`}>{project?.name || 'Project'}</Link>
            <span className="breadcrumb-sep">›</span>
            <span title={video?.name || video?.file_name}>{video?.name || video?.file_name}</span>
          </div>
          {selected && (
            <div className="breadcrumb-actions">
              <button
                className="header-action-btn"
                onClick={handleCopyToMedia}
                disabled={copying}
              >
                {copying ? '⏳ Regenerating...' : '📋 Regenerate Shorts'}
              </button>
              <Link
                to={`/projects/${projectId}/video/${videoId}/clip/${selected.id}`}
                className="header-action-btn"
              >
                ✂ Edit Clip
              </Link>
              <button
                className="header-action-btn"
                onClick={() => setEditField({ id: selected.id, field: 'title', value: selected.title })}
              >
                ✏️ Edit Short
              </button>
              <Link
                to={`/posts/compose?projectId=${encodeURIComponent(projectId || '')}&videoId=${encodeURIComponent(videoId || '')}&shortId=${encodeURIComponent(selected.id)}`}
                className="header-action-btn"
              >
                📤 Publish
              </Link>
            </div>
          )}
        </div>

        {/* Main area: 70% player+detail / 30% tabs+content */}
        <div className="editor-main-area">

          {/* Player + Right Panel (always visible) */}
          <div className="editor-player-detail-wrapper">
            <div className="editor-player">
              {(() => {
                const shortSrc = selected?.fileUrl ? (selected.fileUrl.startsWith('http') ? selected.fileUrl : `${API_BASE}${selected.fileUrl}`) : null;
                const fullSrc = video?.file_path ? `${API_BASE}/uploads/${video.file_path}` : null;
                const videoSrc = shortSrc || fullSrc;
                return videoSrc ? (
                  <video key={videoSrc} src={videoSrc} controls className="editor-video" />
                ) : (
                  <div className="editor-video-placeholder">
                    <span>▶</span>
                    <p>Select a short to preview</p>
                  </div>
                );
              })()}
            </div>

            {/* Right panel — single stable wrapper, inner content swaps per tab */}
            <div className="editor-short-detail" style={{background: 'var(--bg-card)', border: '1px solid var(--border-primary)', borderRadius: 14, padding: 0, boxShadow: '0 8px 32px rgba(0,0,0,0.3)', height: '100%', overflowY: 'auto', display: 'flex', flexDirection: 'column'}}>
              <div className="editor-short-detail-header">
                {activeTab === 'shorts' && selected ? (
                  <>
                    <h3>Short Details</h3>
                    <span className="short-score-pill">Score {normalizeShortScore(selected.score)}/100</span>
                  </>
                ) : activeTab === 'transcript' ? (
                  <>
                    <h3>Transcript</h3>
                    {video?.transcript?.subtitleDownloadUrl && (
                      <a
                        href={video.transcript.subtitleDownloadUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="short-detail-header-btn"
                      >
                        Download Subtitle
                      </a>
                    )}
                  </>
                ) : (
                  <h3>Analysis</h3>
                )}
              </div>
              <div className="editor-short-detail-body">
                {activeTab === 'shorts' && selected && (
                  <>
                    <div className="short-detail-row">
                      <span className="short-detail-label">Title</span>
                      {editField?.id === selected.id && editField.field === 'title' ? (
                        <input
                          className="short-inline-edit"
                          value={editField.value}
                          onChange={e => setEditField({ ...editField, value: e.target.value })}
                          onBlur={() => handleSaveField(selected.id, 'title', editField.value)}
                          onKeyDown={e => { if (e.key === 'Enter') handleSaveField(selected.id, 'title', editField.value); if (e.key === 'Escape') setEditField(null); }}
                          autoFocus
                        />
                      ) : (
                        <span className="short-detail-value" onClick={() => setEditField({ id: selected.id, field: 'title', value: selected.title })}>
                          {selected.title} <span className="edit-hint">✏️</span>
                        </span>
                      )}
                    </div>

                    <div className="short-detail-row">
                      <span className="short-detail-label">Caption</span>
                      {editField?.id === selected.id && editField.field === 'caption' ? (
                        <textarea
                          className="short-inline-edit"
                          value={editField.value}
                          onChange={e => setEditField({ ...editField, value: e.target.value })}
                          onBlur={() => handleSaveField(selected.id, 'caption', editField.value)}
                          onKeyDown={e => { if (e.key === 'Escape') setEditField(null); }}
                          autoFocus
                          rows={3}
                        />
                      ) : (
                        <span className="short-detail-value" onClick={() => setEditField({ id: selected.id, field: 'caption', value: selected.caption })}>
                          {selected.caption} <span className="edit-hint">✏️</span>
                        </span>
                      )}
                    </div>

                    <div className="short-detail-row">
                      <span className="short-detail-label">Tags</span>
                      {editField?.id === selected.id && editField.field === 'tags' ? (
                        <input
                          className="short-inline-edit"
                          value={editField.value}
                          onChange={e => setEditField({ ...editField, value: e.target.value })}
                          onBlur={() => handleSaveField(selected.id, 'tags', editField.value)}
                          onKeyDown={e => { if (e.key === 'Enter') handleSaveField(selected.id, 'tags', editField.value); if (e.key === 'Escape') setEditField(null); }}
                          autoFocus
                          placeholder="tag1, tag2, tag3"
                        />
                      ) : (
                        <span className="short-detail-value" onClick={() => setEditField({ id: selected.id, field: 'tags', value: (selected.tags || []).join(', ') })}>
                          {(selected.tags || []).map((t, i) => <span key={i} className="tag-chip">{t}</span>)}
                          <span className="edit-hint">✏️</span>
                        </span>
                      )}
                    </div>

                    <div className="short-detail-row">
                      <span className="short-detail-label">Time Range</span>
                      <span className="short-detail-value">
                        {formatTime(selected.startTime)} → {formatTime(selected.endTime)} ({formatTime(selected.duration)})
                      </span>
                    </div>

                    <div className="short-detail-row">
                      <span className="short-detail-label">Platform</span>
                      <span className="short-detail-value">{video?.platform?.replace('_', ' ')}</span>
                    </div>

                    <div className="short-detail-row">
                      <span className="short-detail-label">Status</span>
                      <span className="short-detail-value">{video?.job_status}</span>
                    </div>

                    <div className="short-detail-row">
                      <span className="short-detail-label">Total Shorts</span>
                      <span className="short-detail-value">{video?.shorts.length || 0}</span>
                    </div>


                  </>
                )}

                {activeTab === 'transcript' && (
                  video?.transcript?.fullText ? (
                    <pre className="transcript-text">{video.transcript.fullText}</pre>
                  ) : (
                    <p className="transcript-empty">No transcript available.</p>
                  )
                )}

                {activeTab === 'analysis' && (
                  video?.analysis?.topics && video.analysis.topics.length > 0 ? (
                    <div className="analysis-topics">
                      <h4>Topics</h4>
                      {video.analysis.topics.map((t: any, i: number) => (
                        <div key={i} className="topic-row">
                          <span className="topic-time">{formatTime(t.start)} – {formatTime(t.end)}</span>
                          <span className="topic-summary">{t.summary}</span>
                          <span className="topic-score">{t.score}/10</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="transcript-empty">No analysis data available.</p>
                  )
                )}
              </div>
            </div>
          </div>

          {/* Tab bar + shorts grid in bottom 30% */}
          <div className="editor-tab-area">
            <div className="editor-tabs">
              <button className={activeTab === 'shorts' ? 'active' : ''} onClick={() => setActiveTab('shorts')}>
                Shorts ({video?.shorts.length || 0})
              </button>
              <button className={activeTab === 'transcript' ? 'active' : ''} onClick={() => setActiveTab('transcript')}>
                Transcript
              </button>
              <button className={activeTab === 'analysis' ? 'active' : ''} onClick={() => setActiveTab('analysis')}>
                Analysis
              </button>
            </div>

            {activeTab === 'shorts' && (
              <div className="editor-shorts-grid">
                {video?.shorts.map(s => (
                  <div
                    key={s.id}
                    className={`editor-short-card ${selectedShort === s.id ? 'active' : ''}`}
                    onClick={() => setSelectedShort(s.id)}
                  >
                    {s.fileUrl ? (
                      <video className="short-card-thumb" src={s.fileUrl.startsWith('http') ? s.fileUrl : `${API_BASE}${s.fileUrl}`} muted preload="metadata" />
                    ) : (
                      <div className="short-card-thumb-fallback">🎬</div>
                    )}
                    <div className="short-card-overlay" />
                    <div className="short-card-score">{normalizeShortScore(s.score)}%</div>
                    <div className="short-card-info">
                      <div className="short-card-title">{s.title || `Short ${s.index}`}</div>
                      <div className="short-card-caption">{s.caption}</div>
                    </div>
                    <div className="short-card-duration">{formatTime(s.duration)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>

      </main>
    </div>
  );
};

export default VideoEditor;
